import React, { KeyboardEvent, useRef, useState } from 'react';

import { InputAdornment, TextField } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import _ from 'lodash';

import { OhsFormFieldBaseProps } from 'global-components/form/services/OhsFormModels';
import { ModuleType } from 'global-services/constants/OhsObject';
import DragAndDropSelectItem from 'global-components/dnd/OhsDragAndDropDisplay';
import OhsUserLocalServices from 'user/OhsUserLocalServices';
import { getModuleName } from 'user/OhsUserServices';

import OhsFormFieldLayout from '../../components/OhsFormFieldLayout';
import { SelectFieldOptionType } from '../OhsSelectField';
import { OhsUser } from '../../../../user/OhsUser';
import SearchResultMenu from './SearchResultMenu';
import OhsHiddenField from '../../components/OhsHiddenField';
import OhsDragAndDrop from '../../../dnd/OhsDragAndDrop';
import { returnFieldStyle, returnVariant } from '../../components/OhsFieldLayout';
import { LinkItemFormat } from './OhsLinkModels';
import { returnUniqueCustomLink } from './OhsHrContractorLinkServices';

const MaxResults = 10;

const defaultProps = {
  maxCount: undefined,
};

export const LinkTypeSelectOptions = (user?: OhsUser): SelectFieldOptionType[] => [
  { name: 'Link to All', value: 'ALL' },
  { name: `Link to ${user?.configs.hr?.TITLE ?? ''}`, value: ModuleType.HR },
  {
    name: `Link to ${user?.configs.contractor?.TITLE ?? ''}`,
    value: ModuleType.Contractor,
  },
];

export interface Props extends OhsFormFieldBaseProps {
  lookupList: LinkItemFormat[];
  maxCount?: number;
}

function InputEle(props: Props) {
  const user = OhsUserLocalServices.getLocalOhsUser();
  const links = (props.ohsFormSettings.useFormMethods.watch(props.id) ?? []) as LinkItemFormat[];

  const handleArrange = (list: string[]) => {
    props.ohsFormSettings.useFormMethods.setValue(props.id, [...list]);
  };

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [searchText, setSearchText] = React.useState('');

  const [ohsLinkList, ohsSetLinkList] = useState<LinkItemFormat[]>([]);

  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [displayRange, setDisplayRange] = useState({ top: -1, bottom: -1 });
  const paperRef = useRef<any>();
  const keyTypes = { arrowDown: 'ArrowDown', arrowUp: 'ArrowUp', enter: 'Enter' };

  const handleSearchChange = (event: any) => {
    const value = (event.target.value as string) ?? '';
    if (value && value.length > 1) {
      const words = value.split(' ');
      let counter = 0;
      const newResults = new Array<LinkItemFormat>();
      for (let index = 0; index < props.lookupList.length; index += 1) {
        const link = props.lookupList[index];
        let found = true;
        words.forEach((word) => {
          if (JSON.stringify(link).toLocaleLowerCase().indexOf(word.toLocaleLowerCase()) < 0) {
            found = false;
          }
        });
        if (found === true) {
          newResults.push(link);
          counter += 1;
        }

        if (counter === MaxResults) break;
      }

      const sortLinks = (result: any) => {
        const linksWithSort = (links || []).map((link: LinkItemFormat) => ({
          ...link,
          sortingLabel: returnUniqueCustomLink(link),
        }));
        const newLinksWithSort = result.map((link: LinkItemFormat) => ({
          ...link,
          sortingLabel: returnUniqueCustomLink(link),
        }));
        _.pullAllBy(newLinksWithSort, linksWithSort, 'sortingLabel');
        const finalResult = newLinksWithSort.map((link: LinkItemFormat) => {
          return _.omit(link, ['sortingLabel']);
        });

        return finalResult;
      };

      // Hide options that are already selected
      ohsSetLinkList(sortLinks(newResults));

      setAnchorEl(event.currentTarget);
    }
    setSearchText(value);
  };
  const handleAddLink = (link: LinkItemFormat) => {
    const newLink = {
      name: link?.name,
      type: link?.type,
      _id: link?._id,
    };
    props.ohsFormSettings.useFormMethods.setValue(props.id, [...links, newLink]);
    props.ohsFormSettings.useFormMethods.trigger(props.id);
    setSearchText('');
  };
  const handleDeleteLink = (link: LinkItemFormat) => {
    props.ohsFormSettings.useFormMethods.setValue(props.id, [
      ...links.filter((item) => returnUniqueCustomLink(item) !== returnUniqueCustomLink(link)),
    ]);
    props.ohsFormSettings.useFormMethods.trigger(props.id);
  };

  const returnLinkData = (link: LinkItemFormat) => {
    const moduleName = getModuleName(user?.configs, link?.type);
    const linkDetails = link.name;
    const detail = link.description;

    return {
      label: `${moduleName} ${linkDetails}`,
      description: detail,
    };
  };

  const handleMenuClose = () => {
    setHighlightedIndex(-1);
    setDisplayRange({ top: -1, bottom: -1 });
    paperRef.current?.scrollTo(0, 0);
    setAnchorEl(null);
  };

  const handleManualScroll = (keyType: string, index: number) => {
    const itemHeight = 84.95;
    if (paperRef.current) {
      const { offsetHeight } = paperRef.current;
      const size = Math.floor(offsetHeight / itemHeight);
      if (keyType === keyTypes.arrowDown) {
        setDisplayRange((pre) => ({ ...pre, bottom: pre.bottom + 1 }));
        if (index >= size) {
          setDisplayRange((pre) => ({ ...pre, top: pre.top + 1 }));
          paperRef.current.scrollBy(0, itemHeight);
        }
      }
      if (keyType === keyTypes.arrowUp) {
        setDisplayRange((pre) => ({ ...pre, bottom: pre.bottom - 1 }));
        if (index >= 0 && displayRange.bottom <= displayRange.top) {
          setDisplayRange((pre) => ({ ...pre, top: pre.top - 1 }));
          paperRef.current.scrollBy(0, -itemHeight);
        }
      }
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    const lookupList = [...ohsLinkList];
    if (event.key === keyTypes.arrowDown && highlightedIndex <= lookupList.length - 2) {
      setHighlightedIndex((prev) => prev + 1);
      handleManualScroll(keyTypes.arrowDown, highlightedIndex + 1);
    } else if (event.key === keyTypes.arrowUp && highlightedIndex > 0) {
      setHighlightedIndex((prev) => prev - 1);
      handleManualScroll(keyTypes.arrowUp, highlightedIndex - 1);
    } else if (
      event.key === keyTypes.enter &&
      highlightedIndex > -1 &&
      highlightedIndex < lookupList.length
    ) {
      const item: any = lookupList.find((temp, index) => index === highlightedIndex);
      handleAddLink(item);
      handleMenuClose();
    } else if (event.key === 'Escape') handleMenuClose();
  };

  const handleRefFetch = (ref: any) => {
    if (ref && ref.current) {
      paperRef.current = ref.current;
    }
  };

  React.useEffect(() => {
    props.ohsFormSettings.useFormMethods.register(props.id, {
      validate: {
        notEmpty: () => {
          const value = props.ohsFormSettings.useFormMethods.getValues(props.id) ?? [];
          if (props.required === true) {
            if (value && value.length > 0) {
              return true;
            }
          } else {
            return true;
          }

          return false;
        },
        noDuplicated: () => {
          const linkList = props.ohsFormSettings.useFormMethods.getValues(props.id) ?? [];

          return !(linkList || []).some((link: LinkItemFormat, index: number) =>
            linkList.slice(index + 1).some((newLink: LinkItemFormat) => newLink._id === link._id)
          );
        },
      },
    });
    const linkValues = props.ohsFormSettings.useFormMethods.getValues(props.id);
    props.ohsFormSettings.useFormMethods.setValue(props.id, linkValues);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.id, props.required]);

  const limitReached = !!(props.maxCount && props.maxCount <= (links || []).length);

  return (
    <>
      <OhsHiddenField register={props.ohsFormSettings.useFormMethods.register(props.id)} />

      {!props.disabled && (
        <TextField
          type="text"
          fullWidth
          margin="dense"
          placeholder="Search..."
          InputLabelProps={{
            shrink: true,
          }}
          size="small"
          onChange={handleSearchChange}
          onKeyDown={(event: KeyboardEvent) => {
            handleKeyDown(event);
          }}
          variant={returnVariant(props.ohsFormSettings.layout)}
          disabled={props.disabled || limitReached}
          value={searchText}
          style={returnFieldStyle(props.ohsFormSettings.layout)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon />
              </InputAdornment>
            ),
            autoComplete: 'off',
          }}
        />
      )}
      <SearchResultMenu
        anchorEl={anchorEl}
        handleMenuClose={handleMenuClose}
        ohsHrLookupList={[]}
        ohsContractorList={[]}
        ohsLinkList={ohsLinkList}
        setAnchorEl={setAnchorEl}
        handleAddLink={handleAddLink}
        hoveredItem={highlightedIndex}
        handleRefFetch={handleRefFetch}
        handleKeyDownFunc={handleKeyDown}
      />
      <div style={{ marginTop: '5px' }}>
        <OhsDragAndDrop
          list={links || []}
          setList={handleArrange}
          withHandle
          renderItem={(item: LinkItemFormat) => {
            const targetLink = returnLinkData(item);
            return (
              <DragAndDropSelectItem
                label={targetLink?.label}
                description={targetLink?.description}
                onDelete={() => handleDeleteLink(item)}
                disabled={props.disabled}
              />
            );
          }}
        />
      </div>
    </>
  );
}

InputEle.defaultProps = defaultProps;
export default function OhsCustomLinkField(props: Props) {
  return <OhsFormFieldLayout {...props} inputEle={<InputEle {...props} />} />;
}

OhsCustomLinkField.defaultProps = defaultProps;
